export const CLIENT_CSV_HEADERS = [
  'Code',
  'Name',
  'Status',
  'Description',
  'Domain',
  'Email Domains',
  'Contact Emails',
  'Database Name',
  'Database Provision Status',
  'Created At',
  'Updated At',
] as const

export const CLIENT_CSV_TRASH_HEADERS = [...CLIENT_CSV_HEADERS, 'Deleted At'] as const

const DOMAIN_TYPES = ['corporate', 'ticketing_platform', 'msp_shared'] as const

export type ClientEmailDomainCsvRow = {
  domain?: string
  type?: string
  is_primary?: boolean
}

export type ClientContactEmailCsvRow = {
  email?: string
}

export function encodeEmailDomains(rows: ClientEmailDomainCsvRow[] | undefined | null): string {
  if (!rows?.length) return ''

  return rows
    .map((row) => {
      const domain = String(row.domain ?? '').trim().toLowerCase()
      if (!domain) return null

      let type = String(row.type ?? 'corporate').trim().toLowerCase()
      if (!DOMAIN_TYPES.includes(type as (typeof DOMAIN_TYPES)[number])) {
        type = 'corporate'
      }

      const isPrimary = row.is_primary ? '1' : '0'
      return `${domain};${type};${isPrimary}`
    })
    .filter((part): part is string => part !== null)
    .join('|')
}

export function decodeEmailDomains(value: string | null | undefined): ClientEmailDomainCsvRow[] {
  const raw = String(value ?? '').trim()
  if (!raw) return []

  return raw
    .split('|')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [domain = '', type = 'corporate', primary = '0'] = entry.split(';').map((s) => s.trim())
      const normalizedType = DOMAIN_TYPES.includes(type.toLowerCase() as (typeof DOMAIN_TYPES)[number])
        ? type.toLowerCase()
        : 'corporate'

      return {
        domain: domain.toLowerCase(),
        type: normalizedType,
        is_primary: ['1', 'true', 'yes'].includes(primary.toLowerCase()),
      }
    })
    .filter((row) => row.domain)
}

export function encodeContactEmails(rows: ClientContactEmailCsvRow[] | undefined | null): string {
  if (!rows?.length) return ''

  const seen = new Set<string>()
  const parts: string[] = []

  for (const row of rows) {
    const email = String(row.email ?? '').trim().toLowerCase()
    if (!email || seen.has(email)) continue
    seen.add(email)
    parts.push(email)
  }

  return parts.join('|')
}

export function formatClientStatusForExport(status: boolean | number | string | null | undefined): string {
  if (status === true || status === 1 || status === '1') return 'Active'
  if (status === false || status === 0 || status === '0') return 'Inactive'
  if (typeof status === 'string' && status.toLowerCase() === 'active') return 'Active'
  if (typeof status === 'string' && status.toLowerCase() === 'inactive') return 'Inactive'
  return status ? 'Active' : 'Inactive'
}

export function mapClientToCsvRow(client: Record<string, unknown>, includeDeletedAt = false): string[] {
  const row: string[] = [
    String(client.code ?? ''),
    String(client.name ?? ''),
    formatClientStatusForExport(client.status as boolean | string | number | null | undefined),
    String(client.description ?? ''),
    String(client.domain ?? ''),
    encodeEmailDomains((client.email_domains as ClientEmailDomainCsvRow[]) ?? []),
    encodeContactEmails((client.contact_emails as ClientContactEmailCsvRow[]) ?? []),
    String(client.database_name ?? ''),
    String(client.database_provision_status ?? ''),
    String(client.created_at ?? ''),
    String(client.updated_at ?? ''),
  ]

  if (includeDeletedAt) {
    row.push(String(client.deleted_at ?? ''))
  }

  return row
}

export const CLIENT_CSV_TEMPLATE_EXAMPLE_ROW: string[] = [
  'ACME',
  'Acme Corporation',
  'Active',
  'Optional description',
  'acme.com.ph',
  'acme.com.ph;corporate;1|dxc.com;msp_shared;0',
  'support@acme.com.ph|helpdesk@acme.com.ph',
  '',
  '',
  '',
  '',
]
